import { useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  BarChart3, Eye, PieChart, FileSpreadsheet, QrCode, MoreHorizontal, RefreshCw, Copy,
} from 'lucide-react'
import { toast } from 'sonner'
import { useDashboardReportes, useExportarExcelReporte, useDescargarQRReporte } from '../hooks/useReportes'
import { MetricCard } from '../components/reportes/MetricCard'
import { useTeam } from '@/shared/context/TeamContext'

export function ReportesListPage() {
  useDocumentTitle('Reportes de Encuestas')
  const navigate = useNavigate()

  // Filtrar por área seleccionada
  const { selectedArea, selectedTeam } = useTeam()
  const isClientArea = selectedTeam !== 'Admin' && selectedTeam !== 'Tonal'
  const areaGrupoId = isClientArea ? selectedArea?.laboratorio_id : undefined

  const { data, isLoading, refetch } = useDashboardReportes({ grupo_laboratorio_id: areaGrupoId ?? undefined })
  const excelMutation = useExportarExcelReporte()
  const qrMutation = useDescargarQRReporte()

  const handleCopyUrl = async (uuid: string) => {
    const url = `${window.location.origin}/encuestas/responder/${uuid}`
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
      } else {
        throw new Error('clipboard not available')
      }
    } catch {
      setTimeout(() => {
        const input = document.createElement('input')
        input.value = url
        input.style.position = 'fixed'
        input.style.left = '-9999px'
        document.body.appendChild(input)
        input.focus()
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
      }, 100)
    }
    toast.success('URL copiada al portapapeles')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-black dark:text-white" />
          Reportes de Encuestas
        </h1>
        <Button variant="ghost" size="icon" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-96" />
        </div>
      ) : data ? (
        <>
          {/* Metric cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard label="Total encuestas" value={data.total_encuestas} icon={BarChart3} />
            <MetricCard label="Total visualizaciones" value={data.total_vistas} icon={Eye} />
            <MetricCard label="Total inicios" value={data.total_inicios} icon={PieChart} />
            <MetricCard label="Total respuestas" value={data.total_respuestas} icon={BarChart3} />
          </div>

          {/* Table */}
          <Card className="border border-gray-300 dark:border-gray-700">
            {data.encuestas.length > 0 ? (
              <div className="p-6">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                      <TableHead>Título</TableHead>
                      <TableHead className="text-center">Visualizaciones</TableHead>
                      <TableHead className="text-center">Inicios</TableHead>
                      <TableHead className="text-center">Respuestas</TableHead>
                      <TableHead className="text-center">Conversión</TableHead>
                      <TableHead className="text-center">Finalización</TableHead>
                      <TableHead className="text-center">Abandono</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.encuestas.map((encuesta) => (
                      <TableRow key={encuesta.id}>
                        <TableCell className="font-medium max-w-[200px] truncate">
                          <button
                            className="text-left hover:underline hover:text-blue-600 transition-colors"
                            onClick={() => navigate(`/encuestas/reportes/${encuesta.id}/datos`)}
                          >
                            {encuesta.titulo}
                          </button>
                        </TableCell>
                        <TableCell className="text-center">{encuesta.total_visualizaciones}</TableCell>
                        <TableCell className="text-center">{encuesta.total_inicios}</TableCell>
                        <TableCell className="text-center">{encuesta.total_respuestas}</TableCell>
                        <TableCell className="text-center">{encuesta.tasa_conversion.toFixed(1)}%</TableCell>
                        <TableCell className="text-center">{encuesta.tasa_finalizacion.toFixed(1)}%</TableCell>
                        <TableCell className="text-center">{encuesta.tasa_abandono.toFixed(1)}%</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleCopyUrl(encuesta.uuid)}>
                                <Copy className="mr-2 h-4 w-4" /> Copiar URL
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/encuestas/reportes/${encuesta.id}/datos`)}>
                                <Eye className="mr-2 h-4 w-4" /> Ver datos
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/encuestas/reportes/${encuesta.id}/estadisticas`)}>
                                <PieChart className="mr-2 h-4 w-4" /> Ver estadísticas
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => excelMutation.mutate(encuesta.id)}>
                                <FileSpreadsheet className="mr-2 h-4 w-4" /> Exportar Excel
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => qrMutation.mutate(encuesta.id)}>
                                <QrCode className="mr-2 h-4 w-4" /> Descargar QR
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <BarChart3 className="mx-auto h-12 w-12 text-teal-500" />
                <h3 className="mt-4 text-lg font-semibold">Sin datos de reportes</h3>
                <p className="mt-2 text-sm text-muted-foreground">Las encuestas con respuestas aparecerán aquí</p>
              </div>
            )}
          </Card>
        </>
      ) : null}
    </div>
  )
}
